'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  notFound,
  usePathname,
  useRouter,
  useSearchParams
} from 'next/navigation';

import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { format } from 'date-fns';
import he from 'he';
import {
  getDescriptionContentWithoutImages,
  getMagentoSlider
} from 'src/utils/getMagentoSlider';

import {
  useGTMDataLayer,
  useListProductWithFilterPage,
  useStore
} from '@hooks';

import {
  GetCategoryQuery,
  GetCmsBlocksDocument,
  GetFilterInputsQueryQueryResult,
  ProductSearchQueryResult
} from '@generated/graphql';

import { BreadcrumbsCategory } from '@components/common/Breadcrumbs';
import { CountDown } from '@components/common/CountDown';
import { DynamicImage } from '@components/common/DynamicImage';
import { Pagination } from '@components/common/Pagination';
import Portal from '@components/common/PortalContent';
import { ProductCard } from '@components/common/ProductCard';
import { RichContent } from '@components/common/RichContent';
import { Slider } from '@components/common/Slider';
import { SubscribeEmailToNewsletter } from '@components/common/SubscribeEmailToNewsletter';

import { EmptyResult } from './EmptyResult';
import { FilterDesktop } from './FilterDesktop';
import { FilterMobile } from './FilterMobile';
import { ResultText } from './ResultText';
import { SortProducts } from './SortProducts';
import { TextAmountProducts } from './TextAmountProducts';

import {
  Breadcrumb,
  FiltersAggregations,
  SortAttribute,
  SortDirection
} from '@/services/products';

type CategoryPageProps = {
  category?: GetCategoryQuery['categoryList'][number];
  banners?: { desktop: string; mobile: string; href: string };
  filtersAggregations?: FiltersAggregations;
  introspectionData: GetFilterInputsQueryQueryResult['data'];
  productsFromServer?: ProductSearchQueryResult['data'];
  breadcrumbs?: Breadcrumb[];
  filtersFromServer?: {
    inputText: string;
    currentPage: number;
    pageSize: number;
    sortAttribute: SortAttribute;
    sortDirection: SortDirection;
    appliedFilters: {
      [key: string]: any;
    };
  };
} & (
  | {
      isSearchPage: true;
      searchParams: { [key: string]: string | string[] | undefined };
    }
  | { isSearchPage?: false }
);

export const ListProductWithFilter = ({
  category,
  banners,
  introspectionData,
  filtersAggregations,
  breadcrumbs = [],
  productsFromServer: searchProducts,
  ...props
}: CategoryPageProps) => {
  const isSearchPage = props.isSearchPage;
  const pathname = usePathname();
  const urlSearchParams = useSearchParams();
  const searchParams = Object.fromEntries(useSearchParams().entries());

  const { push } = useRouter();
  const { currentStore } = useStore();
  const { pushToDataLayer } = useGTMDataLayer();

  const { filtersList, hasFilters, search, categoriesPath } =
    useListProductWithFilterPage({
      category,
      introspectionData,
      filtersAggregations
    });
  const defaultPage = Number((searchParams?.page as string) || 1);
  const [page, setPage] = useState(defaultPage);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [endDateMobile, setEndDateMobile] = useState<string | null>(null);

  const { data: block } = useQuery(GetCmsBlocksDocument, {
    variables: { identifiers: 'black_friday_cronometro' }
  });

  useEffect(() => {
    if (block?.cmsBlocks?.items?.[0]?.content) {
      const decodedHTML = he.decode(block.cmsBlocks.items[0].content);
      const parser = new DOMParser();
      const doc = parser?.parseFromString(decodedHTML, 'text/html');
      const countdownDiv = doc?.getElementById('black-friday-countdown');
      const countdownDivMobile = doc?.getElementById(
        'black-friday-countdown-mobile'
      );

      if (countdownDiv) {
        const endDateValue = countdownDiv?.getAttribute('data-enddate');
        const endDateValueMobile =
          countdownDivMobile?.getAttribute('data-enddate');
        if (endDateValue) {
          const formatEndDate = format(
            new Date(endDateValue),
            "yyyy-MM-dd'T'HH:mm:ss"
          );
          setEndDate(formatEndDate);
        }
        if (endDateValueMobile) {
          const formatEndDateMobile = format(
            new Date(endDateValueMobile),
            "yyyy-MM-dd'T'HH:mm:ss"
          );
          setEndDateMobile(formatEndDateMobile);
        }
      }
    }
  }, [block]);

  const totalProducts = searchProducts?.products?.total_count || 0;

  const totalPages = searchProducts?.products?.page_info?.total_pages || 0;

  const magentoSlider = useMemo(() => {
    if (!category?.subtitle_category) return { data: [], autoplay: false };
    const result = getMagentoSlider(category.subtitle_category);
    return result;
  }, [category?.subtitle_category]);

  useEffect(() => {
    const pageUrl = searchParams?.page || 1;
    if (searchProducts?.products?.items.length >= 0 && pageUrl == page) {
      if (!isSearchPage) {
        pushToDataLayer('category', {
          category,
          product_list: searchProducts?.products,
          page_number: page,
          results_number: searchProducts?.products.items.length
        });
      } else {
        pushToDataLayer('searchPage', {
          product_list: searchProducts.products,
          page_number: page,
          results_number: searchProducts?.products.items.length
        });
      }
    }
  }, [
    category,
    page,
    pushToDataLayer,
    pathname,
    searchParams?.page,
    searchProducts?.products,
    isSearchPage
  ]);

  useEffect(() => {
    // as the filters change the page to 1, we need to reset the pagination
    if (page !== 1 && searchParams?.page === '1') {
      setPage(defaultPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams?.page]);

  if (!category && !isSearchPage) {
    return notFound();
  }

  return (
    <>
      {magentoSlider.data.length > 0 && (
        <div className="w-full box-border md:px-6 bn_rotativo">
          <Slider infinite autoPlaySpeed={9000} pauseOnHover numberOfSlides={1}>
            {magentoSlider.data.map((item, idx) => {
              return (
                <a key={idx} href={item.href ?? pathname}>
                  <DynamicImage
                    desktopSrc={item.desktop}
                    mobileSrc={item.mobile}
                    alt={`slide-image-${idx}`}
                    priority={idx === 0}
                    mobileProps={{
                      width: 412,
                      height: 400
                    }}
                    desktopProps={{
                      width: 1280,
                      height: 438
                    }}
                  />
                </a>
              );
            })}
          </Slider>
        </div>
      )}

      {category &&
        category.description &&
        category?.display_mode !== 'PRODUCTS' && (
          <>
            <Portal id="black-friday-countdown">
              <section className="relative">
                <section className="absolute py-5 -top-18 -right-56 font-gotham">
                  <CountDown
                    endDate={endDate}
                    variant="black-friday"
                    fontSize="text-[80px]"
                    showDays
                  />
                </section>
              </section>
            </Portal>
            <Portal id="black-friday-countdown-mobile">
              <div className="flex py-5 flex-col justify-end h-[100%] font-gotham">
                <CountDown
                  endDate={endDateMobile}
                  variant="black-friday"
                  fontSize="text-[48px]"
                  showDays
                />
              </div>
            </Portal>
            <Portal id="newsletter_form">
              <section className="w-[90%] md:w-[570px] mx-auto">
                <SubscribeEmailToNewsletter
                  label="Cadastrar"
                  originId="category-products-and-page-mode"
                  variant="black-friday"
                />
              </section>
            </Portal>
            {(banners?.desktop || banners?.mobile) && (
              <DynamicImage
                className="aspect-[1/1] lg:aspect-[192/47] w-full"
                desktopSrc={banners.desktop}
                mobileSrc={banners.mobile}
                alt=""
                priority
                desktopProps={{
                  width: 1366,
                  height: 338
                }}
                mobileProps={{
                  width: 412,
                  height: 412,
                  quality: 80
                }}
              />
            )}
            <RichContent
              html={getDescriptionContentWithoutImages(category.description)}
            />
          </>
        )}

      {category?.display_mode !== 'PAGE' && (
        <div
          className="mx-auto w-full px-4 mt-4 mb-8 lg:px-6"
          id="areaListProducts"
        >
          <div>
            <BreadcrumbsCategory
              className="mb-4"
              categoryId={
                category ? category.id : currentStore?.root_category_id
              }
              productName={category ? category.name : ''}
              defaultBreadcrumbs={breadcrumbs}
            />

            <div className="mt-4 lg:flex lg:gap-6">
              <div className="hidden lg:block lg:w-[280px] shrink-0">
                <FilterDesktop filtersList={filtersList} />
              </div>

              <div className="flex-1 min-w-0">
                <div
                  className={classNames('flex flex-col', {
                    'lg:-mt-4': category?.category_description_name
                  })}
                >
                  <h1 className="text-primary-medium font-extrabold text-xl pt-2">
                    {category?.title_category || category?.name}
                  </h1>
                  {category?.subtitle_category && (
                    <h2 className="lg:mt-1 text-base text-gray-dark">
                      {category.subtitle_category}
                    </h2>
                  )}
                </div>

                <div className="hidden lg:block">
                  <div className="grid grid-cols-[1fr_auto] items-center gap-4 lg:mb-6">
                    <div className="min-w-0">
                      <ResultText
                        className="truncate text-gray-dark"
                        search={search}
                        totalProducts={totalProducts}
                      />
                    </div>

                    {(totalProducts > 0 || !isSearchPage) && (
                      <div className="flex items-center gap-3 shrink-0 justify-self-end">
                        <TextAmountProducts
                          search={search}
                          currentPage={page}
                          searchProducts={searchProducts}
                          totalProducts={totalProducts}
                        />
                        <SortProducts />
                      </div>
                    )}
                  </div>
                </div>

                <FilterMobile
                  categoriesPath={categoriesPath}
                  hasFilters={hasFilters}
                  search={search}
                  filtersList={filtersList}
                  totalProducts={totalProducts}
                />
                {totalProducts > 0 && (
                  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    {searchProducts?.products?.items?.map(product => (
                      <ProductCard
                        key={product.id}
                        product={product as any}
                        className="min-h-[488px]"
                      />
                    ))}
                  </div>
                )}
                {totalProducts > 0 && (
                  <div className="flex items-center mt-[18px] justify-end">
                    <TextAmountProducts
                      search={search}
                      currentPage={page}
                      searchProducts={searchProducts}
                      totalProducts={totalProducts}
                    />
                    <Pagination
                      page={page}
                      setPage={setPage}
                      current={page}
                      total={totalPages}
                      onPageChange={selectedPage => {
                        const nextURLParams = new URLSearchParams(
                          urlSearchParams.toString()
                        );

                        nextURLParams.set('page', selectedPage.toString());

                        push(`${pathname}?${nextURLParams.toString()}`);
                      }}
                    />
                  </div>
                )}
                {totalProducts === 0 && (
                  <EmptyResult searchText={searchParams.query as string} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {category &&
        category.description_2 &&
        category?.display_mode !== 'PRODUCTS' && (
          <RichContent html={category.description_2} />
        )}
    </>
  );
};
